/**
 * /rate/$token — avaliação CSAT PÚBLICA (sem AuthGate, fetch sem bearer):
 * GET /public/rate/:token mostra número/título; 5 estrelas grandes +
 * comentário opcional + Enviar (POST). Estados: já respondida (agradece),
 * token inválido (aviso) e sucesso (obrigado).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, CircleAlert, Star } from 'lucide-react';
import type { ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Label, Textarea } from '@/components/ui/input';
import { PageSpinner, Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { PublicBrandMark } from '@/shared/brand-mark';
import { errorMessage, publicRatingQuery, usePublicRate } from './portal-queries';

const SCORE_LABELS: Record<number, string> = {
  1: 'Péssimo',
  2: 'Ruim',
  3: 'Regular',
  4: 'Bom',
  5: 'Ótimo',
};

/** Shell público centrado — mesmo padrão visual do /kb e do widget de chat. */
function RateShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-background">
      <header className="flex flex-col items-center gap-1 pt-10 pb-8">
        <PublicBrandMark className="text-2xl text-sidebar" iconClassName="size-6" />
        <span className="text-sm text-muted-foreground">Avaliação de atendimento</span>
      </header>
      <main className="mx-auto w-full max-w-lg px-4 pb-16">{children}</main>
    </div>
  );
}

function Notice({
  icon: Icon,
  tone,
  title,
  message,
}: {
  icon: typeof CircleAlert;
  tone: 'success' | 'muted';
  title: string;
  message: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border bg-card p-8 text-center shadow-xs">
      <Icon
        className={cn('size-10', tone === 'success' ? 'text-success' : 'text-muted-foreground/50')}
        strokeWidth={1.5}
        aria-hidden
      />
      <p className="text-base font-semibold text-foreground">{title}</p>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

/** Estrelas grandes clicáveis com pré-visualização no hover. */
function BigStars({
  value,
  hover,
  onHover,
  onSelect,
  disabled,
}: {
  value: number | null;
  hover: number | null;
  onHover: (n: number | null) => void;
  onSelect: (n: number) => void;
  disabled?: boolean;
}) {
  const shown = hover ?? value;
  return (
    <div
      role="radiogroup"
      aria-label="Nota do atendimento"
      className="flex items-center justify-center gap-1"
      onMouseLeave={() => onHover(null)}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = shown !== null && n <= shown;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            disabled={disabled}
            onClick={() => onSelect(n)}
            onMouseEnter={() => onHover(n)}
            onFocus={() => onHover(n)}
            onBlur={() => onHover(null)}
            aria-label={`${n} ${n === 1 ? 'estrela' : 'estrelas'} — ${SCORE_LABELS[n]}`}
            className="rounded-md p-1 transition-transform outline-none hover:scale-110 focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
          >
            <Star
              aria-hidden
              className={cn(
                'size-9 transition-colors',
                filled ? 'fill-[#FAAD14] text-[#FAAD14]' : 'text-muted-foreground/40'
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

export function RatePage() {
  // Rota /rate/$token registrada pelo router (W1) — param não tipado aqui.
  const params = useParams({ strict: false }) as { token?: string };
  const token = params.token ?? '';
  const view = useQuery(publicRatingQuery(token));
  const rate = usePublicRate(token);

  const [score, setScore] = useState<number | null>(null);
  const [hoverScore, setHoverScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [sent, setSent] = useState(false);
  const [alreadyRated, setAlreadyRated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (score === null || rate.isPending) return;
    setError(null);
    rate.mutate(
      { score, comment: comment.trim() || undefined },
      {
        onSuccess: () => setSent(true),
        onError: (e) => {
          if ((e as unknown as ApiError).status === 409) {
            setAlreadyRated(true);
            return;
          }
          setError(`Não foi possível enviar sua avaliação: ${errorMessage(e)}`);
        },
      }
    );
  };

  if (view.isPending) {
    return (
      <RateShell>
        <PageSpinner />
      </RateShell>
    );
  }

  if (view.isError) {
    const status = (view.error as unknown as ApiError).status;
    const invalid = status === 404 || status === 410 || status === 400;
    return (
      <RateShell>
        <Notice
          icon={CircleAlert}
          tone="muted"
          title={invalid ? 'Link de avaliação inválido' : 'Não foi possível carregar'}
          message={
            invalid
              ? 'Este link de avaliação não existe ou expirou.'
              : `Tente novamente em instantes. (${errorMessage(view.error)})`
          }
        />
      </RateShell>
    );
  }

  if (sent) {
    return (
      <RateShell>
        <Notice
          icon={CheckCircle2}
          tone="success"
          title="Obrigado pela sua avaliação!"
          message="Sua opinião nos ajuda a melhorar o atendimento."
        />
      </RateShell>
    );
  }

  if (view.data.responded || alreadyRated) {
    return (
      <RateShell>
        <Notice
          icon={CheckCircle2}
          tone="success"
          title="Avaliação já registrada"
          message="Este atendimento já foi avaliado. Obrigado!"
        />
      </RateShell>
    );
  }

  return (
    <RateShell>
      <div className="rounded-lg border bg-card p-6 shadow-xs">
        <p className="text-center text-sm text-muted-foreground">
          Como você avalia o atendimento do ticket
        </p>
        <p className="mt-1 text-center text-base font-semibold text-foreground">
          <span className="mr-1.5 text-primary tabular-nums">#{view.data.ticketNumber}</span>
          {view.data.title}
        </p>

        <div className="mt-5">
          <BigStars
            value={score}
            hover={hoverScore}
            onHover={setHoverScore}
            onSelect={setScore}
            disabled={rate.isPending}
          />
          <p
            className="mt-1.5 h-5 text-center text-sm font-medium text-muted-foreground"
            aria-live="polite"
          >
            {hoverScore !== null || score !== null
              ? SCORE_LABELS[(hoverScore ?? score) as number]
              : ' '}
          </p>
        </div>

        <div className="mt-3 flex flex-col gap-1.5">
          <Label htmlFor="rate-comment" className="text-[13px] font-semibold">
            Comentário (opcional)
          </Label>
          <Textarea
            id="rate-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Conte como foi sua experiência…"
            className="min-h-20"
          />
        </div>

        {error ? (
          <p className="mt-2 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <Button
          className="mt-4 h-10 w-full"
          onClick={submit}
          disabled={score === null || rate.isPending}
        >
          {rate.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
          Enviar avaliação
        </Button>
      </div>
    </RateShell>
  );
}
